import React, { useEffect } from "react";
import {
  Box, Typography, Chip, Grid, Card, CardContent, Alert, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, CircularProgress, Button
} from "@mui/material";
import { Refresh, CheckCircle } from "@mui/icons-material";
import { useCampaignReport } from "../hooks/useCampaign";
import { countSmsParts, formatBdt } from "../utils/smsParts";
import { Section } from "./CampaignComponents";

const pct = (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`;
const dhaka = (d) => new Date(d).toLocaleString("en-US", { timeZone: "Asia/Dhaka", dateStyle: "medium", timeStyle: "short" });

const SKIP_REASONS = {
  frequency_cap: "already got enough SMS this week",
  opted_out: "turned promotional messages off",
  do_not_call: "on the do-not-call list",
  invalid_number: "invalid number",
  stopped: "campaign stopped"
};

const Metric = ({ label, value, sub, color }) => (
  <Grid item xs={6} md={3}>
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ color, fontWeight: 600 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  </Grid>
);

// Personal top-up offer given by an automation run: exact, each paid top-up carries its offer
const OfferFunnel = ({ offer }) =>
  offer ? (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Wallet top-up offer</Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Step label="Given" value={offer.given} sub={offer.running ? `${offer.running} still running` : undefined} />
        <Step label="Saw it" value={offer.viewed} />
        <Step label="Started top-up" value={offer.started} />
        <Step label="Paid with it" value={offer.used} sub={`${formatBdt(offer.paid)} paid · ${formatBdt(offer.bonus)} bonus`} />
      </Box>
    </Box>
  ) : null;

const Step = ({ label, value, sub }) => (
  <Box sx={{ flex: "1 1 120px", textAlign: "center", p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6">{(value || 0).toLocaleString()}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Box>
);

/** Results per send hour / A/B group */
export const BreakdownTable = ({ title, rows, showCost = true, firstColumn = "Group" }) => (
  <>
    <Typography variant="subtitle2" gutterBottom>{title}</Typography>
    <TableContainer sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{firstColumn}</TableCell>
            <TableCell align="right">People</TableCell>
            <TableCell align="right">Paid</TableCell>
            <TableCell align="right">Paid rate</TableCell>
            <TableCell align="right">Revenue</TableCell>
            <TableCell align="right">Revenue per person</TableCell>
            {showCost && <TableCell align="right">Cost</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.label}>
              <TableCell>{r.label}</TableCell>
              <TableCell align="right">{r.people.toLocaleString()}</TableCell>
              <TableCell align="right">{r.payers}</TableCell>
              <TableCell align="right">{pct(r.payerRate)}</TableCell>
              <TableCell align="right">{formatBdt(r.revenue)}</TableCell>
              <TableCell align="right">{formatBdt(r.revenuePerPerson)}</TableCell>
              {showCost && <TableCell align="right">{formatBdt(r.cost)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

/** Campaign results: did it make more money than it cost? (SMS or push) */
const CampaignResults = ({ campaign, channel = "sms" }) => {
  const { report, loading, error, fetch } = useCampaignReport();
  const isSms = channel === "sms";
  const title = isSms ? "SMS Results" : "Push Results";

  useEffect(() => { fetch(campaign._id, channel); }, [campaign._id, campaign.status, channel]);

  const header = (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
      <Typography variant="h6">{title}</Typography>
      <Button size="small" startIcon={<Refresh />} onClick={() => fetch(campaign._id, channel)} disabled={loading}>Refresh</Button>
    </Box>
  );

  if (loading && !report) {
    return <Section>{header}<Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={28} /></Box></Section>;
  }
  if (error) return <Section>{header}<Alert severity="error">{error}</Alert></Section>;
  if (!report) return null;

  const skippedLine = report.skipped > 0 && (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Skipped {report.skipped}: {Object.entries(report.skippedReasons || {}).map(([r, n]) => `${n} ${SKIP_REASONS[r] || r}`).join(", ")}.
    </Typography>
  );

  if (!report.hasData) {
    const parts = countSmsParts(campaign.smsMessage?.content || "").parts;
    return (
      <Section>
        {header}
        <Typography variant="body2" color="text.secondary">
          {report.reason === "not_sent"
            ? "Results appear here after the campaign is sent."
            : report.reason === "scheduled"
              ? `${report.scheduled} message(s) are waiting for their send time.`
              : `Nothing was sent on this channel, so there is nothing to measure.`}
        </Typography>
        {skippedLine}
        <OfferFunnel offer={report.offer} />
        {isSms && report.reason === "not_sent" && parts > 0 && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Cost: {parts} SMS part{parts === 1 ? "" : "s"} × {formatBdt(report.rate)} = <b>{formatBdt(parts * report.rate)}</b> per person
            {campaign.smsMessage?.content?.includes("{TrackingLink}") ? " (plus the link, which may add a part)" : ""}.
          </Typography>
        )}
      </Section>
    );
  }

  const { delivery, sentGroup, holdoutGroup, method, profit, extraRevenue, windowDays } = report;
  const madeMoney = profit >= 0;

  return (
    <Section>
      {header}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Chip color={madeMoney ? "success" : "error"} label={madeMoney ? `Made ${formatBdt(profit)}` : `Lost ${formatBdt(-profit)}`} />
        <Chip variant="outlined" label={`Payments within ${windowDays} day${windowDays === 1 ? "" : "s"}`} />
        {report.windowOpenUntil && <Chip color="info" variant="outlined" label={`Still counting until ${dhaka(report.windowOpenUntil)}`} />}
        {delivery.scheduled > 0 && <Chip color="warning" variant="outlined" label={`${delivery.scheduled} still waiting to send`} />}
      </Box>

      <OfferFunnel offer={report.offer} />

      {method === "attributed" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No holdout group: this counts every payment by people who got the message, including people who would have paid anyway,
          so the real profit is probably lower. Use a holdout on the next {isSms ? "SMS-only" : "push-only"} campaign to measure it properly.
        </Alert>
      )}
      {report.smallSample && (
        <Alert severity="info" sx={{ mb: 2 }}>Small numbers: treat this result as a rough signal, not a final answer.</Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Metric
          label={isSms ? "SMS cost" : "Push cost"}
          value={formatBdt(delivery.cost)}
          sub={isSms
            ? delivery.costEstimated
              ? "Estimated from message length"
              : delivery.actualSpend !== null && delivery.actualSpend !== undefined
                ? `BulkSMSBD balance moved ${formatBdt(delivery.actualSpend)}`
                : `${formatBdt(report.rate)} per part`
            : "Push is free"}
        />
        <Metric label="Revenue from recipients" value={formatBdt(sentGroup.revenue)} sub={`${sentGroup.payers} payer${sentGroup.payers === 1 ? "" : "s"}`} />
        <Metric
          label={method === "holdout" ? `Extra revenue from the ${isSms ? "SMS" : "push"}` : "Attributed revenue"}
          value={formatBdt(extraRevenue)}
          sub={method === "holdout" ? "Compared with the holdout group" : "Not compared with a holdout"}
        />
        <Metric label={madeMoney ? "Profit" : "Loss"} value={formatBdt(Math.abs(profit))} color={madeMoney ? "success.main" : "error.main"}
          sub={delivery.cost > 0 && extraRevenue > 0 ? `Revenue = ${(extraRevenue / delivery.cost).toFixed(1)}× the cost` : undefined} />
      </Grid>

      <Typography variant="subtitle2" gutterBottom>Funnel</Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        <Step label={isSms ? "SMS sent" : "Push sent"} value={delivery.sent} sub={delivery.failed ? `${delivery.failed} failed` : undefined} />
        <Step label={isSms ? "Clicked link" : "Opened"} value={delivery.clicked} sub={delivery.sent ? pct(delivery.clicked / delivery.sent) : undefined} />
        {isSms && <Step label="Opened the app" value={delivery.appOpened} sub={delivery.installed ? `${delivery.installed} installed` : undefined} />}
        <Step label="Paid" value={sentGroup.payers} sub={pct(sentGroup.payerRate)} />
        {holdoutGroup && delivery.holdout > 0 && (
          <Step label="Holdout (not sent) paid" value={holdoutGroup.payers} sub={`of ${delivery.holdout.toLocaleString()} · ${pct(holdoutGroup.payerRate)}`} />
        )}
      </Box>
      {skippedLine}
      <Box sx={{ mb: 2 }} />

      {delivery.holdout > 0 && (
        <BreakdownTable
          title="Got it vs. holdout"
          showCost={false}
          rows={[
            { label: `Got the ${isSms ? "SMS" : "push"}`, people: delivery.sent, payers: sentGroup.payers, payerRate: sentGroup.payerRate, revenue: sentGroup.revenue, revenuePerPerson: sentGroup.revenuePerUser },
            { label: "Holdout (not sent)", people: delivery.holdout, payers: holdoutGroup.payers, payerRate: holdoutGroup.payerRate, revenue: holdoutGroup.revenue, revenuePerPerson: holdoutGroup.revenuePerUser }
          ]}
        />
      )}

      {report.abTest && <BreakdownTable title="Send-time test (A/B)" rows={report.abTest} showCost={isSms} />}
      {report.bySlot?.length > 1 && <BreakdownTable title="By send hour" firstColumn="Sent at" rows={report.bySlot} showCost={isSms} />}

      <Typography variant="subtitle2" gutterBottom>People who paid after the {isSms ? "SMS" : "push"} ({sentGroup.payers})</Typography>
      {report.converted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Nobody yet.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>First payment</TableCell>
                <TableCell align="center">{isSms ? "Clicked link" : "Opened"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.converted.map((u) => (
                <TableRow key={u.userId}>
                  <TableCell>{u.name || "—"}</TableCell>
                  <TableCell>{u.mobile || "—"}</TableCell>
                  <TableCell align="right">{formatBdt(u.amount)}</TableCell>
                  <TableCell>{dhaka(u.paidAt)}</TableCell>
                  <TableCell align="center">{u.clicked ? <CheckCircle fontSize="small" color="success" /> : ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Counts completed bKash payments and wallet top-ups. If someone got a later {isSms ? "SMS" : "push"} from another campaign before paying, that payment goes to the later campaign.
      </Typography>
    </Section>
  );
};

export default CampaignResults;
